import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CALENDLY_URL } from "./constants";

const EASE = [0.16, 1, 0.3, 1];

// Slow, subtle scroll reveal
export const Reveal = ({ children, className, delay = 0, y = 40 }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 1.1, ease: EASE, delay }}
    viewport={{ once: true, margin: "-8% 0px -8% 0px" }}
  >
    {children}
  </motion.div>
);

// Gold double-dash divider
export const Divider = ({ className = "" }) => (
  <div
    className={`flex items-center justify-center gap-3 my-12 ${className}`}
    aria-hidden="true"
  >
    <span className="block w-10 h-px bg-[#C9A84C]" />
    <span className="block w-10 h-px bg-[#C9A84C]" />
  </div>
);

export const Eyebrow = ({ children, className = "" }) => (
  <span
    className={`block text-[0.7rem] sm:text-xs tracking-[0.28em] uppercase text-[#C9A84C] font-sans font-medium ${className}`}
  >
    {children}
  </span>
);

// Outlined CTA button. `to` = internal route (react-router), `href` = external
// URL override (defaults to CALENDLY_URL). tone: gold | navy. size: md | sm.
export const CTAButton = ({
  children,
  testId,
  ember = false,
  to,
  href,
  tone = "gold",
  size = "md",
}) => {
  const label =
    typeof children === "string" ? children : "Book an alignment call";
  const pad =
    size === "sm"
      ? "px-7 sm:px-8 py-3 text-[0.7rem] sm:text-xs"
      : "px-9 sm:px-11 py-4 text-xs sm:text-sm";
  const toneCls =
    tone === "navy"
      ? "border-[#1A2535] text-[#1A2535] hover:bg-[#1A2535]/[0.05]"
      : "border-[#C9A84C] text-[#C9A84C] hover:bg-[#C9A84C]/[0.06]";
  const emberCls = ember
    ? "hover:shadow-[0_0_34px_rgba(184,84,30,0.45)] hover:border-[#D4691E] hover:text-[#E9C87A]"
    : tone === "gold"
    ? "hover:shadow-[0_0_28px_rgba(201,168,76,0.28)]"
    : "";
  const cls = `group inline-flex items-center justify-center border ${toneCls} ${pad} uppercase tracking-[0.18em] font-sans font-medium transition-all duration-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/60 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ${emberCls}`;

  if (to) {
    return (
      <Link to={to} data-testid={testId} aria-label={label} className={cls}>
        {children}
      </Link>
    );
  }
  return (
    <a
      href={href || CALENDLY_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      aria-label={label}
      className={cls}
    >
      {children}
    </a>
  );
};

// Section wrapper with dark / light variants
export const Section = ({ id, variant = "dark", className = "", children }) => {
  const bg =
    variant === "dark"
      ? "bg-[#0D1B2A] text-[#F5F0E8]"
      : "bg-[#F0EAE0] text-[#1A2535]";
  return (
    <section
      id={id}
      className={`relative w-full px-6 py-24 sm:py-32 lg:py-40 ${bg} ${className}`}
    >
      <div className="max-w-3xl mx-auto">{children}</div>
    </section>
  );
};

// Shared footer
export const SiteFooter = () => (
  <footer
    data-testid="site-footer"
    className="bg-[#0D1B2A] border-t border-[#C9A84C]/15 text-center px-6 py-20"
  >
    <p className="font-serif text-3xl tracking-[0.3em] text-[#C9A84C]">AC</p>
    <p className="mt-6 font-serif text-2xl tracking-[0.2em] text-[#F5F0E8] uppercase">
      Aaron Christos
    </p>
    <p className="mt-4 font-serif italic text-[#F5F0E8]/70 text-lg">
      Be a better man. Have better relationships.
    </p>
    <p className="mt-10 font-sans text-[0.65rem] tracking-[0.2em] uppercase text-[#F5F0E8]/40">
      Hobart, Tasmania, Australia
    </p>
  </footer>
);
